import React, { useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { srConfig } from '@config';
import sr from '@utils/sr';
import { usePrefersReducedMotion } from '@hooks';

const scroll = keyframes`
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(calc(-50% - 1rem));
  }
`;

const scrollReverse = keyframes`
  0% {
    transform: translateX(calc(-50% - 1rem));
  }
  100% {
    transform: translateX(0);
  }
`;

const StyledSkillsSection = styled.section`
  max-width: 1000px;
  margin: 0 auto;
  overflow: hidden;
  margin-bottom: 120px;

  @media (max-width: 768px) {
    padding: 60px 0;
  }

  @media (max-width: 480px) {
    padding: 40px 0;
  }
`;

const StyledSkillsContainer = styled.div`
  width: 100%;
  overflow: hidden;
  margin-top: 40px;
  mask-image: linear-gradient(
    to right,
    transparent,
    white 10%,
    white 90%,
    transparent
  );
  -webkit-mask-image: linear-gradient(
    to right,
    transparent,
    white 10%,
    white 90%,
    transparent
  );
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const StyledSkillsTrack = styled.div`
  display: flex;
  gap: 20px;
  width: max-content;
  animation: ${props => props.reverse ? scrollReverse : scroll} 40s linear infinite;

  &:hover {
    animation-play-state: paused;
  }
`;

const StyledSkillPill = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px 24px;
  background: var(--light-navy);
  border: 1px solid var(--lightest-navy);
  border-radius: 50px;
  color: var(--slate);
  font-family: var(--font-sans);
  font-size: var(--fz-md);
  font-weight: 500;
  white-space: nowrap;
  transition: var(--transition);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);

  &:hover {
    background: var(--lightest-navy);
    color: var(--green);
    border-color: var(--green);
    transform: translateY(-2px);
    box-shadow: var(--teal-glow);
  }

  @media (max-width: 768px) {
    font-size: var(--fz-sm);
    padding: 10px 20px;
  }
`;

const Skills = () => {
  const revealContainer = useRef(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    sr.reveal(revealContainer.current, srConfig());
  }, []);

  const skillsRow1 = [
    'JavaScript', 'TypeScript', 'Python', 'Java', 'C', 'SQL',
    'React', 'Next.js', 'Vite', 'HTML', 'CSS', 'Tailwind',
    'Node.js', 'Express.js', 'Flask', 'REST APIs', 'GraphQL'
  ];

  const skillsRow2 = [
    'PostgreSQL', 'MongoDB', 'Redis', 'Prisma ORM', 'Firebase',
    'AWS (EC2, S3, IAM, VPC)', 'Git', 'GitHub Actions', 'CI/CD',
    'Vercel', 'Docker', 'OpenAI API', 'Claude Code', 'scikit-learn',
    'JWT Auth', 'RBAC', 'Postman', 'Figma'
  ];

  const duplicatedRow1 = [...skillsRow1, ...skillsRow1];
  const duplicatedRow2 = [...skillsRow2, ...skillsRow2];

  return (
    <StyledSkillsSection id="skills" ref={revealContainer}>
      <h2 className="numbered-heading">Technical Arsenal</h2>
      <StyledSkillsContainer>
        <StyledSkillsTrack reverse={false}>
          {duplicatedRow1.map((skill, index) => (
            <StyledSkillPill key={index}>
              {skill}
            </StyledSkillPill>
          ))}
        </StyledSkillsTrack>
        <StyledSkillsTrack reverse={true}>
          {duplicatedRow2.map((skill, index) => (
            <StyledSkillPill key={index}>
              {skill}
            </StyledSkillPill>
          ))}
        </StyledSkillsTrack>
      </StyledSkillsContainer>
    </StyledSkillsSection>
  );
};

export default Skills;
